import { useState } from "react";

import type { DBProject } from "@/lib/db-types";
import type { BudgetLineRow, OfferMetadata, TimeEntry, VoucherAllocationRow } from "@/hooks/use-project-detail";
import { groupAllocationsByBudgetLine } from "@/lib/voucher-allocations";
import type { UserLookup } from "@/hooks/use-users";
import { isOverheadLine, isRabattLine, isTaskLine } from "@/lib/billing-calculations";
import { partitionTimetildelingLines, groupLinesByPhase } from "@/lib/timetildeling-grouping";
import { cn } from "@/lib/utils";
import { BudgetCostsSection } from "./BudgetCostsSection";
import { BudgetSummaryFooter } from "./BudgetSummaryFooter";
import { TimetildelingTableBody } from "./TimetildelingTableBody";
import { Button } from "@/components/ui/button";
import { useSystemConfigNumber } from "@/hooks/use-system-config";
import { usePermissions } from "@/hooks/use-permissions";
import { useBudgetLineLoggedHours } from "@/hooks/use-budget-line-logged-hours";
import { useUpsells } from "@/hooks/use-upsells";
import iconDiamondDark from "@/assets/icons/revemork/_diamond_revedark.svg";
import { SrIcon } from "@/components/ui/SrIcon";
import { CollapsibleCard } from "@/components/shared/CollapsibleCard";
import ImportBudgetDialog from "./ImportBudgetDialog";
import FilePreviewDialog from "@/components/shared/FilePreviewDialog";

interface BudgetTabContentProps {
  project: DBProject;
  budgetLines: BudgetLineRow[];
  timeEntries: TimeEntry[];
  voucherAllocations?: VoucherAllocationRow[];
  allUsers: UserLookup[];
  editingBudgetLineId: string | null;
  setEditingBudgetLineId: (id: string | null) => void;
  internalReadOnly: boolean;
  canAssignHours: boolean;
  offerMetadata: OfferMetadata | null;
}

export function BudgetTabContent({
  project: p,
  budgetLines,
  timeEntries,
  voucherAllocations = [],
  allUsers,
  editingBudgetLineId,
  setEditingBudgetLineId,
  internalReadOnly,
  canAssignHours,
}: BudgetTabContentProps) {
  const [importOpen, setImportOpen] = useState(false);
  const [offerPreviewOpen, setOfferPreviewOpen] = useState(false);
  const { data: hourlyRate = 2000 } = useSystemConfigNumber("hourly_rate");
  const { isSuperadmin } = usePermissions();
  const { data: upsells = [] } = useUpsells(p.id);

  // VIB-995/VIB-1151: del linjene i hovedbudsjett (per fase), tilvalg-rubrikk
  // og én rubrikk per GODKJENT mersalg. Ikke-godkjente mersalg holdes helt ute.
  const { regularLines, mainLines, optionalGroups, upsellGroups } =
    partitionTimetildelingLines(budgetLines, upsells);
  // Overhead/rabatt brukes ikke i tabellen, men referansene beholdes så
  // CI-typesjekker er glade hvis fremtidige kort vil vise dem.
  void isOverheadLine;
  void isRabattLine;

  // VIB-997 (tidl. VIB-545): Summer bilag-kost per budsjettlinje for
  // "x bilag · y kr"-indikator. Ett bilag fordeles nå over flere linjer via
  // voucher_allocations, så vi grupperer på allocation.budget_line_id
  // (approved-bilag) i stedet for voucher.budget_line_id.
  const vouchersByBudgetLine = groupAllocationsByBudgetLine(voucherAllocations);

  const budgetLineLoggedHours = useBudgetLineLoggedHours(timeEntries, budgetLines);

  // Faser med oppgavelinjer (intern + timer). Phases uten oppgaver dropper
  // vi i hovedtabellen — kostnader vises i egen seksjon nederst.
  //
  // VIB-723: Linjer med ukjent eller tom phase samles i en "annet"-gruppe
  // så de ikke forsvinner fra Timetildeling. Tidligere ble de filtrert
  // bort, og avveik fra Godkjenne-fanen som viste dem alltid.
  const phasesInOrder = groupLinesByPhase(mainLines).filter((g) =>
    g.lines.some(isTaskLine),
  );

  // Alle kostnadslinjer på tvers av faser, samles nederst.
  const allCostLines = regularLines.filter((bl) => !isTaskLine(bl));

  // Totals used by BudgetSummaryFooter at the bottom of the card.
  const totalAssignedHoursAllPhases = regularLines
    .filter((l) => l.cost_type === "internal")
    .reduce((s, l) => s + (l.quantity_hours ?? 0), 0);
  const totalLogged = [...budgetLineLoggedHours.totals.values()].reduce((s, v) => s + v, 0);
  const totalLoggedRound = Math.round(totalLogged * 10) / 10;

  return (
    <>
    <CollapsibleCard
      title="Timetildeling"
      icon={<SrIcon name="receipt" className="w-4 h-4 text-muted-foreground" />}
      actions={
        <button
          onClick={() => {
            if (p.offer_pdf_url) setOfferPreviewOpen(true);
          }}
          disabled={!p.offer_pdf_url}
          title={p.offer_pdf_url ? "Vis tilbudet" : "Last opp tilbud-PDF i Detaljer-fanen først"}
          className={cn(
            "inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border transition-colors",
            p.offer_pdf_url
              ? "border-border text-muted-foreground hover:text-primary hover:border-primary/40"
              : "border-border/50 text-muted-foreground/50 cursor-not-allowed",
          )}
        >
          <SrIcon name="file-text" className="w-3.5 h-3.5" />
          Se tilbud
        </button>
      }
      contentClassName="border-t"
    >
          {budgetLines.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <img src={iconDiamondDark} alt="" className="w-8 h-8 opacity-20" />
              <p className="text-sm text-muted-foreground">Ingen budsjettposter registrert.</p>
              {isSuperadmin && (
                <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
                  <SrIcon name="upload" className="w-3.5 h-3.5 mr-1.5" />
                  Importer fra tilbuds-PDF
                </Button>
              )}
            </div>
          ) : (
            <>
              {isSuperadmin && (
                <div className="flex items-center px-4 py-2 border-b">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => setImportOpen(true)}
                  >
                    <SrIcon name="upload" className="w-3 h-3 mr-1" />
                    Importer fra PDF
                  </Button>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] border-collapse">
                  <colgroup>
                    <col style={{ width: 32 }} />
                    <col />
                    <col style={{ width: 130 }} />
                    <col style={{ width: 70 }} />
                    <col style={{ width: 90 }} />
                    <col style={{ width: 110 }} />
                  </colgroup>

                  <thead>
                    <tr className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b">
                      <th />
                      <th className="text-left py-2 px-2">Oppgave</th>
                      <th className="text-left py-2 px-2">Tildelt til</th>
                      <th className="text-right py-2 px-2">Timer</th>
                      <th className="text-right py-2 px-2">Brukt</th>
                      <th className="text-center py-2 px-2">Forbruk</th>
                    </tr>
                  </thead>

                  <TimetildelingTableBody
                    mainPhaseGroups={phasesInOrder}
                    optionalGroups={optionalGroups}
                    upsellGroups={upsellGroups.map((g) => ({ key: g.upsellId, title: g.title, lines: g.lines }))}
                    projectId={p.id}
                    users={allUsers ?? []}
                    editingId={editingBudgetLineId}
                    setEditingId={setEditingBudgetLineId}
                    readOnly={internalReadOnly}
                    loggedHoursTotals={budgetLineLoggedHours.totals}
                    loggedHoursPerUser={budgetLineLoggedHours.perUser}
                    loggedHoursPerAssignment={budgetLineLoggedHours.perAssignment}
                    entriesPerUser={budgetLineLoggedHours.entriesPerUser}
                    canAssignHours={canAssignHours}
                  />
                </table>
              </div>

              {/* Kostnader nederst — kollapsbar */}
              <BudgetCostsSection
                costLines={allCostLines}
                projectId={p.id}
                readOnly={internalReadOnly}
                hourlyRate={hourlyRate}
                vouchersByBudgetLine={vouchersByBudgetLine}
                canAssignHours={canAssignHours}
              />

              {/* Summary footer — kun timer (operativ) */}
              <BudgetSummaryFooter
                regularLines={regularLines}
                totalLogged={totalLogged}
                totalLoggedRound={totalLoggedRound}
                totalAssignedHoursAllPhases={totalAssignedHoursAllPhases}
              />
            </>
          )}
    </CollapsibleCard>
    {/* Dialogs renderes som søsken siden de portales til root via Radix */}
    <ImportBudgetDialog
      projectId={p.id}
      existingPdfUrl={p.offer_pdf_url ?? null}
      open={importOpen}
      onOpenChange={setImportOpen}
    />
    {p.offer_pdf_url && (
      <FilePreviewDialog
        open={offerPreviewOpen}
        onOpenChange={setOfferPreviewOpen}
        url={p.offer_pdf_url}
        title={`Tilbud — ${p.name}`}
      />
    )}
    </>
  );
}
